"use client";

import React from "react";
import Link from "next/link";
import dynamic from "next/dynamic";
import {
  ChevronRight,
  Car,
  Calendar,
  IndianRupee,
  Wrench,
  Globe,
  Headphones,
  CheckCircle2,
  Sparkles,
  ArrowRight,
} from "lucide-react";
import { Button } from "@/components/ui/button";

const SimplePageBackground = dynamic(
  () => import("@/components/SimplePageBackground").then((m) => m.SimplePageBackground),
  { ssr: true }
);
const IKFooter = dynamic(() => import("@/components/IKFooter").then((m) => m.IKFooter), { ssr: true });

const WHY_CHOOSE_US = [
  {
    icon: Car,
    title: "Wide Range of Vehicles",
    desc: "Choose from a diverse collection of hatchbacks, sedans, SUVs, premium cars, and motorcycles suitable for solo travelers, families, business professionals, and adventure enthusiasts.",
  },
  {
    icon: Calendar,
    title: "Flexible Rental Plans",
    desc: "Whether you need a vehicle for a few hours, a day, a weekend, or an extended trip, we offer flexible rental options to match your schedule.",
  },
  {
    icon: IndianRupee,
    title: "Transparent Pricing",
    desc: "No hidden charges. What you see during booking is what you pay. Our pricing is straightforward, fair, and designed to offer excellent value.",
  },
  {
    icon: Wrench,
    title: "Well-Maintained Fleet",
    desc: "Every vehicle undergoes regular servicing, cleaning, and quality inspections to ensure safety, reliability, and comfort throughout your journey.",
  },
  {
    icon: Globe,
    title: "Easy Online Booking",
    desc: "Book your preferred vehicle in just a few simple steps through our user-friendly website, with instant confirmation and secure payment options.",
  },
  {
    icon: Headphones,
    title: "Customer-Focused Support",
    desc: "Our dedicated support team is available to assist you before, during, and after your rental, ensuring a smooth and stress-free experience.",
  },
];

const DIFFERENCES = [
  "Wide selection of self-drive cars and bikes",
  "Affordable and transparent pricing",
  "Clean, sanitized, and regularly maintained vehicles",
  "Flexible booking durations",
  "Easy online reservation process",
  "Reliable customer support",
  "Secure booking and payment system",
  "Comfortable travel experience for every journey",
];

export default function AboutUsPage() {
  return (
    <SimplePageBackground>
      <div className="container mx-auto px-4 md:px-6 lg:px-8 py-8 flex-1 flex flex-col justify-between">
        <div>
          {/* Breadcrumbs */}
          <nav className="flex items-center flex-wrap gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-500 dark:text-neutral-400 mb-8 font-medium" aria-label="Breadcrumb">
            <Link href="/" className="hover:text-[#FFB51D] dark:hover:text-[#FFB51D] transition-colors">
              Home
            </Link>
            <ChevronRight className="w-3.5 h-3.5 text-neutral-400 dark:text-neutral-600 shrink-0" aria-hidden />
            <span className="text-[#131222] dark:text-white font-semibold">
              About Us
            </span>
          </nav>

          {/* Page Hero Header */}
          <div className="mb-12 text-center max-w-3xl mx-auto animate-fadeIn">
            <h1 className="text-3xl md:text-5xl font-extrabold text-[#131222] dark:text-white tracking-tight mb-4">
              About <span className="text-[#FFB51D]">IK Travels</span>
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400 text-base md:text-lg font-medium tracking-wide">
              Drive Your Journey, Your Way
            </p>
            <div className="h-1 w-20 bg-[#FFB51D] mx-auto rounded-full mt-4" />
          </div>

          {/* Section 1: Introduction Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-stretch mb-16 animate-fadeIn">
            {/* Left Story Card */}
            <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-6 sm:p-8 bg-white dark:bg-neutral-900/30 shadow-md flex flex-col justify-center">
              <h2 className="text-xl md:text-2xl font-bold text-[#131222] dark:text-white mb-4">
                Our Story
              </h2>
              <div className="space-y-4 text-neutral-700 dark:text-neutral-200 text-sm md:text-base leading-relaxed">
                <p>
                  At IK Travels, we believe every journey should be comfortable, convenient, and completely under your control. Whether you're planning a weekend getaway, a business trip, a family vacation, or your daily commute, we provide reliable self-drive cars and bikes that let you travel at your own pace.
                </p>
                <p>
                  Founded with a vision to make vehicle rentals simple, transparent, and affordable, IK Travels offers a modern mobility solution for travelers who value flexibility, privacy, and convenience. Our goal is to provide a seamless booking experience backed by well-maintained vehicles, competitive pricing, and dependable customer support.
                </p>
              </div>
            </div>

            {/* Right Who We Are / Mission Card */}
            <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-6 sm:p-8 bg-white dark:bg-neutral-900/30 shadow-md flex flex-col justify-between gap-6">
              <div>
                <h3 className="text-lg font-bold text-[#131222] dark:text-white mb-2 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-[#FFB51D]" /> Who We Are
                </h3>
                <p className="text-neutral-700 dark:text-neutral-200 text-sm md:text-base leading-relaxed">
                  IK Travels is a trusted self-drive vehicle rental platform offering a wide range of cars and bikes to suit every travel need. From compact hatchbacks for city drives to spacious SUVs for family trips, premium sedans for business travel, and motorcycles for adventurous rides, our fleet is carefully selected to deliver comfort, safety, and performance.
                </p>
              </div>
              <div className="h-px bg-neutral-200/60 dark:bg-neutral-800" />
              <div>
                <h3 className="text-lg font-bold text-[#131222] dark:text-white mb-2 flex items-center gap-2">
                  <CheckCircle2 className="w-5 h-5 text-[#FFB51D]" /> Our Mission
                </h3>
                <p className="text-neutral-700 dark:text-neutral-200 text-sm md:text-base leading-relaxed">
                  Our mission is to make self-drive travel accessible to everyone by providing reliable vehicles, transparent pricing, and exceptional customer service. We aim to give every traveler the freedom to explore without unnecessary restrictions or hidden costs.
                </p>
              </div>
            </div>
          </div>

          {/* Section 2: Why Choose Us Grid */}
          <div className="mb-16">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-bold text-[#131222] dark:text-white mb-3">
                Why Choose IK Travels?
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base max-w-xl mx-auto">
                We understand that renting a vehicle should be easy and hassle-free. That's why we've built our services around the needs of modern travelers.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {WHY_CHOOSE_US.map((item, index) => {
                const IconComponent = item.icon;
                return (
                  <div
                    key={index}
                    className="group rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-6 bg-white dark:bg-neutral-900/30 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 flex flex-col items-start gap-4"
                  >
                    <div className="p-3 bg-[#FFB51D]/10 dark:bg-[#FFB51D]/5 rounded-xl text-[#FFB51D] group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="w-6 h-6" />
                    </div>
                    <div>
                      <h3 className="font-bold text-[#131222] dark:text-white mb-2 text-base md:text-lg">
                        {item.title}
                      </h3>
                      <p className="text-neutral-600 dark:text-neutral-400 text-sm leading-relaxed">
                        {item.desc}
                      </p>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Section 3: Highlights Checklist */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center mb-16">
            <div className="space-y-4">
              <h2 className="text-2xl md:text-3xl font-bold text-[#131222] dark:text-white mb-2">
                What Makes Us Different?
              </h2>
              <p className="text-neutral-600 dark:text-neutral-400 text-sm md:text-base leading-relaxed">
                At IK Travels, we focus on building lasting relationships with our customers by delivering consistent quality, convenience, and trust in every booking. Enjoy the privacy of your own space and the flexibility to create your own itinerary.
              </p>
            </div>
            
            <div className="rounded-2xl border border-neutral-200/60 dark:border-neutral-800/60 p-6 bg-white dark:bg-neutral-900/30 shadow-sm grid grid-cols-1 sm:grid-cols-2 gap-4">
              {DIFFERENCES.map((diff, index) => (
                <div key={index} className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#FFB51D] shrink-0 mt-0.5" />
                  <span className="text-neutral-600 dark:text-neutral-300 text-sm font-medium">
                    {diff}
                  </span>
                </div>
              ))}
            </div>
          </div>

          {/* Section 4: Our Commitment Text Banner */}
          <div className="rounded-2xl bg-neutral-100 dark:bg-neutral-800/40 border border-neutral-200/40 dark:border-neutral-800/40 p-6 sm:p-8 text-center max-w-4xl mx-auto mb-16 animate-fadeIn">
            <h2 className="text-xl md:text-2xl font-bold text-[#131222] dark:text-white mb-3">
              Our Commitment
            </h2>
            <p className="text-neutral-600 dark:text-neutral-300 text-sm md:text-base leading-relaxed">
              Customer satisfaction is at the heart of everything we do. We continuously work to improve our services, expand our fleet, and enhance our technology to provide a better rental experience for every traveler. Whether you're traveling for business, leisure, or everyday convenience, IK Travels is committed to making every journey safe, comfortable, and memorable.
            </p>
          </div>

          {/* Section 5: Premium CTA Box */}
          <div className="rounded-3xl bg-[#131222] dark:bg-neutral-900/80 border border-neutral-800 p-8 sm:p-12 text-center max-w-4xl mx-auto mb-12 shadow-2xl relative overflow-hidden animate-fadeIn">
            {/* Background decorative blurs */}
            <div className="absolute top-0 right-0 w-32 h-32 bg-[#FFB51D]/10 rounded-full -mr-8 -mt-8 blur-2xl" />
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-[#FFB51D]/5 rounded-full -ml-8 -mb-8 blur-2xl" />

            <div className="relative z-10">
              <h2 className="text-2xl md:text-4xl font-extrabold text-white mb-4">
                Start Your Next Journey with IK Travels
              </h2>
              <p className="text-neutral-300 text-sm md:text-base max-w-xl mx-auto mb-6 leading-relaxed">
                Your next adventure begins with the right vehicle. Browse our fleet, choose the vehicle that fits your needs, and experience the freedom of self-drive travel.
              </p>
              <Link href="/fleet">
                <Button className="h-12 px-8 bg-[#FFB51D] hover:bg-[#FFA500] text-[#131222] font-bold rounded-xl shadow-md hover:shadow-lg transition-all duration-300 flex items-center gap-2 mx-auto">
                  Browse Our Fleet <ArrowRight className="w-4 h-4" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>

      <IKFooter />
    </SimplePageBackground>
  );
}
